import React from 'react';
import { Link } from 'react-router-dom';
import { toJalali } from '../../utils/jalali';
import { TRANSFER_TYPE_LABELS } from '../../utils/constants';
import { getFileUrl } from '../../utils/media';

function ClubDot({ club }) {
  const logo = getFileUrl(club?.logo);
  return (
    <span className="grid h-9 w-9 shrink-0 place-items-center overflow-hidden rounded-full bg-white ring-2 ring-emerald-200">
      {logo ? (
        <img src={logo} alt="" className="h-full w-full object-contain p-1" />
      ) : (
        <span className="text-xs font-black text-emerald-600">{(club?.name || '?').charAt(0)}</span>
      )}
    </span>
  );
}

/**
 * سوابق باشگاهی، نقل و انتقال‌ها و آخرین بازی‌های بازیکن (صفحه‌ی عمومی)
 */
export default function PlayerHistory({ player }) {
  const careers = player?.careerHistory || [];
  const transfers = player?.transfers || [];
  const matches = player?.recentMatches || [];

  if (careers.length === 0 && transfers.length === 0 && matches.length === 0) return null;

  return (
    <>
      {(careers.length > 0 || transfers.length > 0) && (
        <section className="mt-10">
          <h2 className="mb-5 text-2xl font-black text-text-dark">سوابق باشگاهی</h2>

          <div className="rounded-3xl border border-slate-100 bg-white p-6">
            {careers.length > 0 && (
              <ol className="relative space-y-6 border-r-2 border-emerald-100 pr-6">
                {careers.map((c, i) => (
                  <li key={c.id} className="relative">
                    <span className="absolute -right-[2.9rem] top-0">
                      <ClubDot club={c.club} />
                    </span>
                    <div className="flex flex-wrap items-center gap-2">
                      <span className="font-black text-slate-800">{c.club?.name}</span>
                      {i === 0 && !c.toDate && (
                        <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-[10px] font-black text-emerald-700">
                          باشگاه فعلی
                        </span>
                      )}
                    </div>
                    <div className="mt-1 text-xs text-slate-400">
                      {toJalali(c.fromDate)} تا {c.toDate ? toJalali(c.toDate) : 'اکنون'}
                    </div>
                  </li>
                ))}
              </ol>
            )}

            {transfers.length > 0 && (
              <div className={careers.length > 0 ? 'mt-8 border-t border-slate-100 pt-6' : ''}>
                <h3 className="mb-3 text-sm font-black text-slate-600">نقل و انتقال‌ها</h3>
                <ul className="space-y-2">
                  {transfers.map((t) => (
                    <li
                      key={t.id}
                      className="flex flex-wrap items-center gap-2 rounded-2xl bg-slate-50 px-4 py-3 text-sm"
                    >
                      <span className="font-bold text-slate-600">{t.fromClub?.name || 'بدون باشگاه'}</span>
                      <span className="text-emerald-500">←</span>
                      <span className="font-black text-slate-800">{t.toClub?.name}</span>
                      <span className="mr-auto rounded-full bg-white px-2.5 py-0.5 text-[11px] font-bold text-slate-500 ring-1 ring-slate-200">
                        {TRANSFER_TYPE_LABELS[t.type] || t.type}
                      </span>
                      <span className="text-xs text-slate-400">{toJalali(t.transferDate)}</span>
                    </li>
                  ))}
                </ul>
              </div>
            )}
          </div>
        </section>
      )}

      {matches.length > 0 && (
        <section className="mt-10">
          <h2 className="mb-5 text-2xl font-black text-text-dark">آخرین بازی‌ها</h2>

          <div className="overflow-hidden rounded-3xl border border-slate-100 bg-white">
            <ul className="divide-y divide-slate-100">
              {matches.map((s) => (
                <li key={s.id} className="flex flex-wrap items-center gap-3 px-5 py-3 text-sm">
                  <span className="min-w-0 flex-1 truncate font-bold text-slate-700">
                    {s.match.homeClub?.name}
                    <span dir="ltr" className="mx-2 rounded-full bg-slate-900 px-2.5 py-0.5 font-mono text-xs text-white">
                      {s.match.result ? `${s.match.result.homeScore} : ${s.match.result.awayScore}` : '—'}
                    </span>
                    {s.match.awayClub?.name}
                  </span>

                  <span className="flex gap-1 text-xs font-black">
                    {s.goals > 0 && <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-emerald-700">⚽ {s.goals}</span>}
                    {s.assists > 0 && <span className="rounded-full bg-sky-100 px-2 py-0.5 text-sky-700">🎯 {s.assists}</span>}
                    {s.yellowCards > 0 && <span className="rounded-full bg-yellow-100 px-2 py-0.5">🟨</span>}
                    {s.redCards > 0 && <span className="rounded-full bg-red-100 px-2 py-0.5">🟥</span>}
                  </span>

                  <span className="text-xs text-slate-400">{toJalali(s.match.date)}</span>
                </li>
              ))}
            </ul>
          </div>
          <p className="mt-2 text-[11px] text-slate-400">
            <Link to="/competitions" className="hover:text-emerald-600">مشاهده‌ی همه‌ی مسابقات</Link>
          </p>
        </section>
      )}
    </>
  );
}
